import React, {forwardRef, useImperativeHandle, useLayoutEffect, useRef} from "react";
import {Textarea, type TextareaProps} from "@chakra-ui/react";
import {
  MAX_NOTE_CHARACTERS,
  MAX_NOTE_LINES,
  limitQCNoteLines,
  type QCProblemTag,
} from "../../models/constants/qcProblemTags.ts";

const INITIAL_ROWS = 2;

const getMaxHeight = (textarea: HTMLTextAreaElement): number => {
  const style = window.getComputedStyle(textarea);
  const lineHeight = Number.parseFloat(style.lineHeight);
  const paddingTop = Number.parseFloat(style.paddingTop);
  const paddingBottom = Number.parseFloat(style.paddingBottom);
  return Number.isNaN(lineHeight) ? 0 : lineHeight * MAX_NOTE_LINES + paddingTop + paddingBottom;
};

const growToContent = (textarea: HTMLTextAreaElement, minHeight: number, maxHeight: number): void => {
  textarea.style.height = "auto";
  const contentHeight = textarea.scrollHeight;
  if (contentHeight <= 0) {
    textarea.style.height = "";
    return;
  }
  const capped = maxHeight > 0 ? Math.min(contentHeight, maxHeight) : contentHeight;
  textarea.style.height = `${Math.max(capped, minHeight)}px`;
  textarea.style.overflowY = maxHeight > 0 && contentHeight > maxHeight ? "auto" : "hidden";
};

interface QCNoteTextareaProps extends Omit<TextareaProps, "onChange" | "rows" | "resize"> {
  value: string;
  onValueChange: (value: string) => void;
}

export interface QCNoteTextareaHandle {
  applyProblemTag: (previousTag: QCProblemTag | null, nextTag: QCProblemTag | null) => boolean;
}

const QCNoteTextarea = forwardRef<QCNoteTextareaHandle, QCNoteTextareaProps>(
  ({value, onValueChange, onKeyDown, onPaste, ...rest}, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const maxHeightRef = useRef(0);
    const minHeightRef = useRef(0);

    useLayoutEffect(() => {
      const element = textareaRef.current;
      if (!element) return;
      if (maxHeightRef.current === 0) {
        minHeightRef.current = element.clientHeight;
        maxHeightRef.current = getMaxHeight(element);
      }
      growToContent(element, minHeightRef.current, maxHeightRef.current);
    }, [value]);

    const commitText = (raw: string, cursor?: number): void => {
      const limited = limitQCNoteLines(raw);
      onValueChange(limited);
      const element = textareaRef.current;
      if (element && cursor !== undefined) {
        requestAnimationFrame(() => {
          const position = Math.min(cursor, limited.length);
          element.setSelectionRange(position, position);
          element.focus();
        });
      }
    };

    useImperativeHandle(ref, () => ({
      applyProblemTag: (previousTag, nextTag) => {
        let newText = value;
        if (previousTag) {
          const prefix = `${previousTag}: `;
          if (newText.startsWith(prefix)) {
            newText = newText.slice(prefix.length);
          }
        }
        if (nextTag) {
          const nextPrefix = `${nextTag}: `;
          if (!newText.startsWith(nextPrefix)) {
            newText = `${nextPrefix}${newText}`;
          }
        }
        if (newText.length > MAX_NOTE_CHARACTERS) return false;
        commitText(newText, newText.length);
        return true;
      }
    }));

    return (
      <Textarea
        ref={textareaRef}
        value={value}
        rows={INITIAL_ROWS}
        maxLength={MAX_NOTE_CHARACTERS}
        resize="none"
        sx={{lineHeight: "1.5"}}
        onChange={(event) => {
          const element = event.target;
          const raw = element.value;
          const cursor = element.selectionStart ?? 0;
          const limited = limitQCNoteLines(raw);
          onValueChange(limited);
          if (limited.length < raw.length) {
            requestAnimationFrame(() => element.setSelectionRange(Math.min(cursor, limited.length), Math.min(cursor, limited.length)));
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && event.currentTarget.value.split("\n").length >= MAX_NOTE_LINES) event.preventDefault();
          onKeyDown?.(event);
        }}
        onPaste={(event) => {
          event.preventDefault();
          const element = event.currentTarget;
          const pasted = event.clipboardData.getData("text");
          const start = element.selectionStart ?? 0;
          const end = element.selectionEnd ?? 0;
          commitText(element.value.slice(0, start) + pasted + element.value.slice(end), start + pasted.length);
          onPaste?.(event);
        }}
        {...rest}
      />
    );
  }
);

QCNoteTextarea.displayName = "QCNoteTextarea";

export default QCNoteTextarea;
